import { Route, Routes } from 'react-router-dom';

import { TemporaryStorageHandler } from 'components/TemporaryStorageHandler';
import { ABExperiment } from 'containers/ABExperiment';
import ErrorBoundary from 'containers/ErrorBoundary';
import { RequireAuthentication } from 'containers/RequireAuthentication';
import SetFlashMessages from 'containers/SetFlashMessages';
import SetGMapsError from 'containers/SetGMapsError';
import { ShowUserModals } from 'containers/ShowUserModals';
import { TabContextProvider } from 'containers/TabContext';
import { UpdateRequiredContextProvider } from 'containers/UpdateRequiredContext';
import { useRouterHashScroll } from 'hooks/useRouterHashScroll';
import { CmsDashboardApiPage } from 'modules/cmsDashboard/api/types/CmsDashboardApiPage';
import { AdminsLinksLoadable } from 'modules/main/pages/AdminsLinks';
import { BuyCreditsLinksLoadable } from 'modules/main/pages/BuyCreditsLinks';
import { CreditsLinksLoadable } from 'modules/main/pages/CreditsLinks';
import { EditOrgLinksLoadable } from 'modules/main/pages/EditOrgLinks';
import { ForgotPasswordPageContainerLoadable } from 'modules/main/pages/ForgotPassword';
import { GradFairsLinksLoadable } from 'modules/main/pages/GradFairsLinks';
import { ImportedListingPageContainerLoadable } from 'modules/main/pages/ImportedListing';
import { IntegrateAtsLinksLoadable } from 'modules/main/pages/IntegrateAtsLinks';
import { InvalidLinkLoadable } from 'modules/main/pages/InvalidLink';
import { InvoiceViewContainerLoadable } from 'modules/main/pages/InvoiceView';
import { InvoicesLinksLoadable } from 'modules/main/pages/InvoicesLinks';
import { LoginPageContainerLoadable } from 'modules/main/pages/Login';
import { NotificationsLinksLoadable } from 'modules/main/pages/NotificationsLinks';
import { RateRecommendedCandidateLoadable } from 'modules/main/pages/RateRecommendedCandidate';
import { ResetPasswordPageContainerLoadable } from 'modules/main/pages/ResetPassword';
import { SignupPageContainerLoadable } from 'modules/main/pages/Signup';
import { UserInviteContainerLoadable } from 'modules/main/pages/UserInvite';
import { OrgDashboardAppLoadable } from 'modules/orgDashboard/OrgDashboardAppLoadable';
import { OrgDashboardCheckoutAppLoadable } from 'modules/orgDashboard/checkout/OrgDashboardCheckoutAppLoadable';
import { orgDashboardCheckoutRouteGroup } from 'modules/orgDashboard/checkout/orgDashboardCheckout.routes';
import { orgDashboardRouteGroup } from 'modules/orgDashboard/orgDashboard.routes';
import { CompensationSurveyLoadable } from 'modules/salaries/pages/CompensationSurvey/CompensationSurveyLoadable';
import { SalaryReportLoadable } from 'modules/salaries/pages/SalaryReport/SalaryReportLoadable';
import { UserDashboardAppLoadable } from 'modules/userDashboard/UserDashboardAppLoadable';
import { CreateOrgPageContainerLoadable } from 'modules/userDashboard/pages/CreateOrg/CreateOrgPageContainerLoadable';
import { CreateOrgWithGradProgramPageContainerLoadable } from 'modules/userDashboard/pages/CreateOrgWithGradProgram/CreateOrgWithGradProgramPageContainerLoadable';
import { ManageGradProgramPageContainerLoadable } from 'modules/userDashboard/pages/ManageGradProgram/ManageGradProgramPageContainerLoadable';
import { UserDashboardPostAListingPageContainerLoadable } from 'modules/userDashboard/pages/PostAListing/UserDashboardPostAListingPageContainerLoadable';
import { SelectOrgDashboardLoadable } from 'modules/userDashboard/pages/SelectOrgDashboard/SelectOrgDashboardLoadable';
import { ThemeGlobalStyle } from 'theme';
import { AmpContext } from 'utils/AmpContext';
import { WithRouterParams } from 'utils/WithRouterParams';
import { configValue } from 'utils/config';
import { SIGN_UP_TO_POST_URL_FOR_LOCALE } from 'utils/constants';

import { getCmsComponent } from './helpers/getCmsComponent';
import {
  mainGradProgramSegmentedRoutes,
  mainInternshipSegmentedRoutes,
  mainJobSegmentedRoutes,
  mainOrgSegmentedRoutes,
  mainVolopSegmentedRoutes,
} from './main.routes';
import { ViewGradProgramPageLoadable } from './pages/ViewGradProgram/ViewGradProgramPageLoadable';
import { ViewInternshipPageLoadable } from './pages/ViewInternship/ViewInternshipPageLoadable';
import { ViewJobPageLoadable } from './pages/ViewJob/ViewJobPageLoadable';
import { ViewOrgPageLoadable } from './pages/ViewOrg/ViewOrgPageLoadable';
import { ViewVolopPageLoadable } from './pages/ViewVolop/ViewVolopPageLoadable';

type Props = {
  content?: CmsDashboardApiPage;
  requestIsAmp?: boolean;
};

export default function MainApp({ content, requestIsAmp }: Props) {
  useRouterHashScroll();

  return (
    <ErrorBoundary>
      <ThemeGlobalStyle />
      <SetGMapsError />
      <SetFlashMessages />
      <TemporaryStorageHandler />
      <ShowUserModals>
        <AmpContext.Provider value={Boolean(requestIsAmp)}>
          <UpdateRequiredContextProvider>
            <TabContextProvider>
              <Routes>
                <Route
                  path="/amp/*"
                  element={getCmsComponent({ content, amp: true })}
                />
                <Route path="/login" element={<LoginPageContainerLoadable />} />
                <Route
                  path="/signup"
                  element={<SignupPageContainerLoadable />}
                />
                <Route
                  path="/forgot-password"
                  element={<ForgotPasswordPageContainerLoadable />}
                />
                <Route
                  path="/reset-password/:token"
                  element={
                    <WithRouterParams
                      render={({ token }) => (
                        <ResetPasswordPageContainerLoadable token={token} />
                      )}
                    />
                  }
                />
                <Route
                  path="/invalid-or-expired-link"
                  element={<InvalidLinkLoadable />}
                />
                <Route
                  path="/salaries/survey"
                  element={<CompensationSurveyLoadable />}
                />
                <Route
                  path="/salaries/explore"
                  element={<SalaryReportLoadable />}
                />
                <Route
                  path="/invoices/:invoiceId"
                  element={
                    <WithRouterParams
                      render={({ invoiceId }) => (
                        <InvoiceViewContainerLoadable invoiceId={invoiceId} />
                      )}
                    />
                  }
                />
                <Route
                  path="/donation-receipts/:invoiceId"
                  element={
                    <WithRouterParams
                      render={({ invoiceId }) => (
                        <InvoiceViewContainerLoadable
                          invoiceId={invoiceId}
                          isDonation
                        />
                      )}
                    />
                  }
                />

                {mainOrgSegmentedRoutes.map((segmentedRoute) => (
                  <Route
                    key={segmentedRoute.fullPath}
                    path={segmentedRoute.fullPath}
                    element={<ViewOrgPageLoadable />}
                  />
                ))}

                {mainJobSegmentedRoutes.map((segmentedRoute) => (
                  <Route
                    key={segmentedRoute.fullPath}
                    path={segmentedRoute.fullPath}
                    element={<ViewJobPageLoadable />}
                  />
                ))}

                {mainInternshipSegmentedRoutes.map((segmentedRoute) => (
                  <Route
                    key={segmentedRoute.fullPath}
                    path={segmentedRoute.fullPath}
                    element={<ViewInternshipPageLoadable />}
                  />
                ))}

                {mainVolopSegmentedRoutes.map((segmentedRoute) => (
                  <Route
                    key={segmentedRoute.fullPath}
                    path={segmentedRoute.fullPath}
                    element={<ViewVolopPageLoadable />}
                  />
                ))}

                <Route
                  path="/:lang/imported-action-opps/:source/:volopId"
                  element={
                    <WithRouterParams
                      render={({ volopId, source }) => (
                        <ImportedListingPageContainerLoadable
                          // @ts-expect-error TS(2322): Type 'string' is not assignable to type 'ImportedL... Remove this comment to see the full error message
                          source={source}
                          listingId={volopId}
                        />
                      )}
                    />
                  }
                />
                <Route
                  path="/user-invites/:inviteId"
                  element={
                    <WithRouterParams
                      render={({ inviteId }) => (
                        <UserInviteContainerLoadable inviteId={inviteId} />
                      )}
                    />
                  }
                />
                <Route
                  path="/dashboard/*"
                  element={
                    <RequireAuthentication>
                      <UserDashboardAppLoadable />
                    </RequireAuthentication>
                  }
                />
                <Route
                  path="/organizations/create"
                  element={
                    <RequireAuthentication
                      loginPathname={
                        SIGN_UP_TO_POST_URL_FOR_LOCALE[CURRENT_LOCALE]
                      }
                    >
                      <CreateOrgPageContainerLoadable />
                    </RequireAuthentication>
                  }
                />
                {configValue('idealist', 'gsdEnabled') === 'true' && (
                  <Route
                    path="/organizations/create-with-grad-program"
                    element={
                      <RequireAuthentication>
                        <CreateOrgWithGradProgramPageContainerLoadable />
                      </RequireAuthentication>
                    }
                  />
                )}
                <Route
                  path={orgDashboardRouteGroup.wrapperPath}
                  element={
                    <RequireAuthentication>
                      <WithRouterParams
                        render={({ orgId }) => (
                          <OrgDashboardAppLoadable orgId={orgId} />
                        )}
                      />
                    </RequireAuthentication>
                  }
                />

                <Route
                  path={orgDashboardCheckoutRouteGroup.fullWrapperPath}
                  element={
                    <RequireAuthentication>
                      <OrgDashboardCheckoutAppLoadable />
                    </RequireAuthentication>
                  }
                />

                <Route
                  path="/post-a-listing"
                  element={
                    <ABExperiment
                      experimentId="POST_A_LISTING_REDIRECT"
                      defaultBucket="SIGN_UP_TO_POST"
                      initializeWithDefault
                      render={(bucket) => (
                        <RequireAuthentication
                          loginPathname={
                            bucket === 'SIGN_UP_TO_POST'
                              ? SIGN_UP_TO_POST_URL_FOR_LOCALE[CURRENT_LOCALE]
                              : getText('/organizations')
                          }
                        >
                          <UserDashboardPostAListingPageContainerLoadable />
                        </RequireAuthentication>
                      )}
                    />
                  }
                />
                <Route
                  path="/select-org"
                  element={
                    <RequireAuthentication>
                      <SelectOrgDashboardLoadable />
                    </RequireAuthentication>
                  }
                />
                <Route
                  path="/manage-grad-program"
                  element={
                    <RequireAuthentication>
                      <ManageGradProgramPageContainerLoadable />
                    </RequireAuthentication>
                  }
                />
                <Route
                  path="/buy-credits"
                  element={
                    <RequireAuthentication>
                      <BuyCreditsLinksLoadable />
                    </RequireAuthentication>
                  }
                />
                <Route
                  path="/integrate-ats"
                  element={
                    <RequireAuthentication>
                      <IntegrateAtsLinksLoadable />
                    </RequireAuthentication>
                  }
                />
                <Route
                  path="/notifications"
                  element={
                    <RequireAuthentication>
                      <NotificationsLinksLoadable />
                    </RequireAuthentication>
                  }
                />
                <Route
                  path="/credits"
                  element={
                    <RequireAuthentication>
                      <CreditsLinksLoadable />
                    </RequireAuthentication>
                  }
                />
                <Route
                  path="/admins"
                  element={
                    <RequireAuthentication>
                      <AdminsLinksLoadable />
                    </RequireAuthentication>
                  }
                />
                <Route
                  path="/edit-org"
                  element={
                    <RequireAuthentication>
                      <EditOrgLinksLoadable />
                    </RequireAuthentication>
                  }
                />
                <Route
                  path="/gradfairs"
                  element={
                    <RequireAuthentication>
                      <GradFairsLinksLoadable />
                    </RequireAuthentication>
                  }
                />
                <Route
                  path="/invoices"
                  element={
                    <RequireAuthentication>
                      <InvoicesLinksLoadable />
                    </RequireAuthentication>
                  }
                />
                <Route
                  path="/email/rate-recommended-candidate/:applicantId/good"
                  element={
                    <RequireAuthentication>
                      <WithRouterParams
                        render={({ applicantId }) => (
                          <RateRecommendedCandidateLoadable
                            applicantId={applicantId}
                            isGood
                          />
                        )}
                      />
                    </RequireAuthentication>
                  }
                />
                <Route
                  path="/email/rate-recommended-candidate/:applicantId/bad"
                  element={
                    <RequireAuthentication>
                      <WithRouterParams
                        render={({ applicantId }) => (
                          <RateRecommendedCandidateLoadable
                            applicantId={applicantId}
                            isGood={false}
                          />
                        )}
                      />
                    </RequireAuthentication>
                  }
                />

                {mainGradProgramSegmentedRoutes.map((segmentedRoute) => (
                  <Route
                    key={segmentedRoute.fullPath}
                    path={segmentedRoute.fullPath}
                    element={<ViewGradProgramPageLoadable />}
                  />
                ))}

                <Route
                  path="/*"
                  element={getCmsComponent({ content, amp: false })}
                />
              </Routes>
            </TabContextProvider>
          </UpdateRequiredContextProvider>
        </AmpContext.Provider>
      </ShowUserModals>
    </ErrorBoundary>
  );
}
